import { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import moment from 'moment';

import { scrollToBlock } from '../../../helpers/scrollToBlock';

// The Search Console property list has one implementation in the app, on the tool that introduced
// it; every GSC tool picks a property out of everything Search Console exposes to the user, which
// is a wider list than the domains linked to GDDash.
import { getUserDomains } from '../../DomainPerformance/query';
import ScopeControls from '../GscTools/components/ScopeControls';
import CoverageStrip from '../GscTools/components/CoverageStrip';
import { ELIGIBLE_PERMISSIONS, probeLatestDate } from '../GscTools/query';
import { downloadCsv } from '../GscTools/csv';
import {
    buildSeries,
    coverageFor,
    joinRows,
    metricsFor,
    reportsPosition,
    rowMetrics,
    sumRows,
} from '../GscTools/metrics';
import { defaultPeriod, formatPeriod, periodEndingOn } from '../GscTools/period';
import { readParams, writeParams } from '../GscTools/url';

import AppearanceContributors from './components/AppearanceContributors';
import ExtraControls from './components/ExtraControls';
import SegmentCoverage from './components/SegmentCoverage';
import SegmentTable from './components/SegmentTable';
import SegmentTrend from './components/SegmentTrend';
import { buildSegmentCsv } from './exportRows';
import {
    fetchAppearancePages,
    fetchAppearances,
    fetchSegmentRows,
    fetchSegmentSeries,
} from './query';
import {
    URL_KEYS,
    hasReportScope,
    initialDraft,
    reportDraftFromParams,
    reportParamsFromScope,
    resolveReportScope,
} from './scope';

const IDLE_SEGMENTS = { status: 'idle', rows: [], returnedRows: 0, error: null };
const IDLE_TREND = { status: 'idle', error: null, current: null, baseline: null };
const IDLE_APPEARANCES = { status: 'idle', rows: [], error: null };
const IDLE_PAGES = { status: 'idle', rows: [], error: null };

const DEFAULT_METRIC = 'clicks';

/**
 * Audience & Search Appearance — one Search Console property's traffic cut by where it was served,
 * what it was served to, and how it appeared in the result.
 *
 * Three cuts of the same window, kept apart on purpose. Country and device partition the
 * property's traffic, so their rows add up to it. Search appearance does not: Search Console
 * counts one impression under every appearance that applied to it, so those rows overlap, are
 * never summed, and are exported as their own file rather than beside the others.
 *
 * None of this is demographics. A country row counts impressions Google served in that country,
 * not people, and no request here asks for — or could receive — anything about who they were.
 * The report says what Search Console returned and stops there.
 *
 * Four requests at most per applied scope, each on its own so one panel's failure stays in that
 * panel: the segment rows for each window, the appearance keys this property actually has, the
 * pages behind one of them, and one segment's daily series when a row is opened. Nothing is
 * persisted — the data lives in this component's state for as long as the page is open, while the
 * scope lives in the page's address, so a reload or a shared link opens the same report and runs
 * it on arrival.
 */
class AudienceAppearance extends Component {
    constructor(props) {
        super(props);

        this.handleDraftChange = this.handleDraftChange.bind(this);
        this.handleApply = this.handleApply.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        this.handleToggleControls = this.handleToggleControls.bind(this);
        this.handleSelectSegment = this.handleSelectSegment.bind(this);
        this.handleClearSegment = this.handleClearSegment.bind(this);
        this.handleMetric = this.handleMetric.bind(this);
        this.handleSelectAppearance = this.handleSelectAppearance.bind(this);
        this.handleClearAppearance = this.handleClearAppearance.bind(this);
        this.handleExport = this.handleExport.bind(this);
        this.handlePrint = this.handlePrint.bind(this);
        this.handleDownloadImage = this.handleDownloadImage.bind(this);

        this.credentialsDomainId = props.credentials_domain_id || '';
        this.reportRef = createRef();
        this.trendRef = createRef();

        // Bumped by every apply and refresh; a response carrying an older value is stale.
        this.generation = 0;
        this.trendGeneration = 0;
        this.appearanceGeneration = 0;
        this.isMounted_ = false;

        const period = defaultPeriod();

        // The address is read once, here, so the controls show the report it names from the first
        // paint rather than flicking from the defaults to it a moment later.
        const params = readParams();
        this.urlDraft = reportDraftFromParams(params);
        this.urlHasScope = hasReportScope(params);

        this.state = {
            properties: [],
            loadingProperties: true,
            propertiesError: null,
            latestDate: '',

            draft: { ...initialDraft(period), ...this.urlDraft },

            applied: null,
            loading: false,
            fetchedAt: null,
            controlsOpen: true,
            capturing: false,

            segments: { ...IDLE_SEGMENTS },
            selectedKey: null,
            metric: DEFAULT_METRIC,
            trend: { ...IDLE_TREND },

            appearances: { ...IDLE_APPEARANCES },
            selectedAppearance: null,
            // Set when a window change removed the appearance that was open, so the block can
            // say the selection was dropped instead of silently emptying itself.
            appearanceCleared: false,
            pages: { ...IDLE_PAGES },
        };
    }

    async componentDidMount() {
        this.isMounted_ = true;

        let properties = [];
        let propertiesError = null;

        try {
            const sites = await getUserDomains();
            properties = sites.filter((site) => ELIGIBLE_PERMISSIONS.has(site.permissionLevel));
        } catch (err) {
            console.log(err);
            propertiesError = err?.message ?? this.props.t('global.something_went_wrong');
        }

        if (!this.isMounted_) return;

        // A link naming a property this account cannot read is not quietly answered with another
        // property's figures: the first one is selected and the scope bar is left open for the
        // user to confirm, rather than a report being run under a scope nobody asked for.
        const requested = this.urlDraft.siteUrl
            ? properties.find((item) => item.siteUrl === this.urlDraft.siteUrl)
            : null;
        const property = requested ?? properties[0];
        const autoApply = this.urlHasScope && !!property && (!this.urlDraft.siteUrl || !!requested);

        this.setState(
            (state) => ({
                properties,
                propertiesError,
                loadingProperties: false,
                draft: property
                    ? {
                          ...state.draft,
                          siteUrl: property.siteUrl,
                          permissionLevel: property.permissionLevel,
                      }
                    : state.draft,
            }),
            () => {
                // Dates that came from the address are the user's own: the probe still runs, for
                // the preset anchor, but it does not move them.
                if (property) {
                    this.suggestDates(property.siteUrl, { seedDates: !this.urlDraft.startDate });
                }
                if (autoApply) this.handleApply();
            },
        );
    }

    componentWillUnmount() {
        this.isMounted_ = false;
    }

    /**
     * Seeds the date controls from the property's most recent activity, so the window the user is
     * offered has something in it rather than ending in days Search Console has not filled yet.
     */
    async suggestDates(siteUrl, { seedDates = true } = {}) {
        if (!this.credentialsDomainId || !siteUrl) return;

        try {
            const latest = await probeLatestDate({
                credentialsDomainId: this.credentialsDomainId,
                siteUrl,
                searchType: this.state.draft.searchType,
            });

            if (!latest || !this.isMounted_) return;

            this.setState((state) => {
                if (state.draft.siteUrl !== siteUrl) return null;
                // Kept as well as applied: the date presets count back from this day, so one
                // click lands on days the property has data for.
                if (!seedDates) return { latestDate: latest };

                return { latestDate: latest, draft: { ...state.draft, ...periodEndingOn(latest) } };
            });
        } catch (err) {
            // A probe that fails costs nothing: the controls keep their default window.
            console.log(err);
        }
    }

    scope() {
        return resolveReportScope(this.state.draft, this.props.t);
    }

    /** The metric the trend draws, held to the ones this surface actually reports. */
    metric() {
        const searchType = this.state.applied?.searchType ?? this.state.draft.searchType;
        return metricsFor(searchType).includes(this.state.metric)
            ? this.state.metric
            : DEFAULT_METRIC;
    }

    handleDraftChange(patch) {
        this.setState(
            (state) => ({ draft: { ...state.draft, ...patch } }),
            () => {
                if (patch.siteUrl) {
                    const property = this.state.properties.find(
                        (item) => item.siteUrl === patch.siteUrl,
                    );
                    this.setState(
                        (state) => ({
                            draft: {
                                ...state.draft,
                                permissionLevel: property?.permissionLevel ?? '',
                            },
                        }),
                        () => this.suggestDates(patch.siteUrl),
                    );
                }
            },
        );
    }

    handleToggleControls() {
        this.setState((state) => ({ controlsOpen: !state.controlsOpen }));
    }

    handleApply() {
        const scope = this.scope();
        if (scope.errors.length) return;
        this.load(scope);
    }

    handleRefresh() {
        if (this.state.applied) this.load(this.state.applied);
    }

    /**
     * Runs the applied scope: the segment rows for each window, and the appearance keys, both
     * started at once and each landing on its own.
     *
     * A segment selected under the previous scope is dropped with the rows it belonged to — its
     * trend was read under filters that may no longer apply. The open appearance is remembered
     * rather than dropped: it is a key, not a row, and if the new window still returns it the
     * block picks it up again where the user left it.
     */
    load(scope) {
        this.generation += 1;
        this.trendGeneration += 1;
        this.appearanceGeneration += 1;
        const generation = this.generation;

        // Written here rather than in handleApply so the address also follows a refresh, and so
        // it only ever describes a scope that was actually run.
        writeParams(reportParamsFromScope(scope), URL_KEYS);

        this.previousAppearance = this.state.selectedAppearance;

        this.setState({
            applied: scope,
            controlsOpen: false,
            loading: true,
            fetchedAt: new Date().toISOString(),

            segments: { ...IDLE_SEGMENTS, status: 'loading' },
            selectedKey: null,
            trend: { ...IDLE_TREND },

            appearances: { ...IDLE_APPEARANCES, status: 'loading' },
            selectedAppearance: null,
            appearanceCleared: false,
            pages: { ...IDLE_PAGES },
        });

        this.loadSegments(scope, generation);
        this.loadAppearances(scope, generation);
    }

    /**
     * Block 01 — one request per window, joined on the exact key Search Console returned.
     *
     * The join is the shared one, so a key present in only one window is kept and marked as not
     * comparable rather than being read as a gain from zero.
     */
    async loadSegments(scope, generation) {
        const forPeriod = (period) =>
            period
                ? fetchSegmentRows({
                      credentialsDomainId: this.credentialsDomainId,
                      scope,
                      period,
                  })
                : Promise.resolve([]);

        try {
            const [current, prior] = await Promise.all([
                forPeriod(scope.current),
                forPeriod(scope.baseline),
            ]);

            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({
                loading: false,
                segments: {
                    status: 'done',
                    rows: joinRows(current, prior, reportsPosition(scope.searchType)),
                    returnedRows: current.length,
                    error: null,
                },
            });
        } catch (err) {
            console.log('segments', err);
            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({
                loading: false,
                segments: {
                    ...IDLE_SEGMENTS,
                    status: 'error',
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                },
            });
        }
    }

    /**
     * Block 03, first request — the appearances this property returned for this window.
     *
     * If the key that was open before this apply is in the list, it is re-opened and its pages
     * fetched; if the window no longer has it, the block is told the selection was cleared rather
     * than being left to show nothing under a heading naming it.
     */
    async loadAppearances(scope, generation) {
        try {
            const rows = await fetchAppearances({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                period: scope.current,
            });

            if (!this.isMounted_ || generation !== this.generation) return;

            const hasPosition = reportsPosition(scope.searchType);
            const appearances = rows.map((row) => ({
                id: row?.keys?.[0] ?? '',
                key: row?.keys?.[0] ?? '',
                ...rowMetrics(row, hasPosition),
            }));

            const previous = this.previousAppearance;
            const stillThere = previous && appearances.some((item) => item.key === previous);

            this.setState(
                {
                    appearances: { status: 'done', rows: appearances, error: null },
                    selectedAppearance: stillThere ? previous : null,
                    appearanceCleared: !!previous && !stillThere,
                },
                () => {
                    if (stillThere) this.loadAppearancePages(scope, previous);
                },
            );
        } catch (err) {
            console.log('appearances', err);
            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({
                appearances: {
                    ...IDLE_APPEARANCES,
                    status: 'error',
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                },
            });
        }
    }

    /**
     * Opening a segment row fetches its own daily series in each window — a separate filtered
     * request, because the rows in block 01 were returned with no date dimension and the days
     * inside them were never sent.
     */
    handleSelectSegment(key) {
        const scope = this.state.applied;
        this.trendGeneration += 1;
        const trendGeneration = this.trendGeneration;

        if (!key || !scope) {
            return this.setState({ selectedKey: null, trend: { ...IDLE_TREND } });
        }

        this.setState(
            { selectedKey: key, trend: { ...IDLE_TREND, status: 'loading' } },
            // Every pick brings the block into view, not just the first. The block it fills sits
            // below the fold, so a pick that left the page where it was would look like nothing
            // happened — and that is as true of the fifth segment as of the first. scrollToBlock
            // moves the page only when the block is not already readable, so a reader already
            // down there is left alone.
            () => scrollToBlock(this.trendRef.current),
        );

        this.loadTrend(scope, key, trendGeneration);
    }

    handleClearSegment() {
        this.trendGeneration += 1;
        this.setState({ selectedKey: null, trend: { ...IDLE_TREND } });
    }

    async loadTrend(scope, segmentKey, trendGeneration) {
        const hasPosition = reportsPosition(scope.searchType);

        const forPeriod = async (period) => {
            if (!period) return null;

            const daily = await fetchSegmentSeries({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                period,
                segmentKey,
            });

            return {
                series: buildSeries(daily, period, hasPosition),
                coverage: coverageFor(daily, period),
                // Summed from the days returned rather than taken from the block 01 row, so the
                // cards and the lines above them are the same figures.
                totals: sumRows(daily, hasPosition),
            };
        };

        try {
            const [current, baseline] = await Promise.all([
                forPeriod(scope.current),
                forPeriod(scope.baseline),
            ]);

            if (!this.isMounted_ || trendGeneration !== this.trendGeneration) return;

            this.setState({ trend: { status: 'done', error: null, current, baseline } });
        } catch (err) {
            console.log('trend', err);
            if (!this.isMounted_ || trendGeneration !== this.trendGeneration) return;

            this.setState({
                trend: {
                    ...IDLE_TREND,
                    status: 'error',
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                },
            });
        }
    }

    handleMetric(metric) {
        this.setState({ metric });
    }

    handleSelectAppearance(appearance) {
        const scope = this.state.applied;
        if (!scope) return;

        // loadAppearancePages puts the block into its loading state; this only records which key
        // that state belongs to.
        this.setState({ selectedAppearance: appearance, appearanceCleared: false });
        this.loadAppearancePages(scope, appearance);
    }

    handleClearAppearance() {
        this.appearanceGeneration += 1;
        this.setState({
            selectedAppearance: null,
            appearanceCleared: false,
            pages: { ...IDLE_PAGES },
        });
    }

    async loadAppearancePages(scope, appearance) {
        this.appearanceGeneration += 1;
        const appearanceGeneration = this.appearanceGeneration;

        this.setState({ pages: { ...IDLE_PAGES, status: 'loading' } });

        try {
            const rows = await fetchAppearancePages({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                period: scope.current,
                appearance,
            });

            if (!this.isMounted_ || appearanceGeneration !== this.appearanceGeneration) return;

            const hasPosition = reportsPosition(scope.searchType);

            this.setState({
                pages: {
                    status: 'done',
                    rows: rows.map((row) => ({
                        id: row?.keys?.[0] ?? '',
                        key: row?.keys?.[0] ?? '',
                        ...rowMetrics(row, hasPosition),
                    })),
                    error: null,
                },
            });
        } catch (err) {
            console.log('appearance pages', err);
            if (!this.isMounted_ || appearanceGeneration !== this.appearanceGeneration) return;

            this.setState({
                pages: {
                    ...IDLE_PAGES,
                    status: 'error',
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                },
            });
        }
    }

    exportName(prefix) {
        const scope = this.state.applied;
        return `${prefix}-${scope?.segment ?? 'segment'}-${scope?.siteUrl ?? 'property'}-${
            scope?.current?.startDate ?? ''
        }`
            .replace(/[^a-z0-9_.-]+/gi, '-')
            .toLowerCase();
    }

    handleExport() {
        const scope = this.state.applied;
        if (!scope || this.state.segments.status !== 'done') return;

        downloadCsv(
            buildSegmentCsv({
                t: this.props.t,
                scope,
                rows: this.state.segments.rows,
                returnedRows: this.state.segments.returnedRows,
                fetchedAt: this.state.fetchedAt
                    ? moment(this.state.fetchedAt).format('YYYY-MM-DD HH:mm')
                    : null,
            }),
            `${this.exportName('audience-appearance')}.csv`,
        );
    }

    handlePrint() {
        window.print();
    }

    /**
     * Captures the loaded report as an image, from the DOM already on screen — no further request
     * and no upload. The controls that produced the report and the export actions themselves are
     * left out of the frame, while the scope and coverage labels stay in it, so the picture still
     * says what it is a picture of.
     */
    async handleDownloadImage() {
        const container = this.reportRef.current;
        if (!container) return;

        this.setState({ capturing: true });

        try {
            const canvas = await html2canvas(container, {
                backgroundColor: '#ffffff',
                scale: 2,
                ignoreElements: (element) =>
                    element.classList?.contains('gsc-tool-controls') ||
                    element.classList?.contains('gsc-tool-controls-bottom') ||
                    element.classList?.contains('gsc-tool-criteria') ||
                    element.classList?.contains('gsc-tool-page-actions') ||
                    // The shared chart and table carry their own refresh and download buttons.
                    element.classList?.contains('chart-action-btns') ||
                    element.classList?.contains('dt-actions__bar'),
            });

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `${this.exportName('audience-appearance')}.png`;
            link.click();
        } catch (err) {
            console.log(err);
        } finally {
            if (this.isMounted_) this.setState({ capturing: false });
        }
    }

    render() {
        const { t } = this.props;
        const scope = this.scope();
        const applied = this.state.applied;
        const ready = this.state.segments.status === 'done';

        return (
            <div className="gsc-tool" ref={this.reportRef}>
                <div className="gsc-tool-title-row">
                    <div>
                        <h2 className="page-title">{t('tools.audience_appearance.header')}</h2>
                        <p className="gsc-tool-sub">{t('tools.audience_appearance.subtitle')}</p>
                    </div>

                    <div className="gsc-tool-page-actions">
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handleDownloadImage}
                            disabled={!ready || this.state.capturing}
                        >
                            <i className="icon-fi_download" />
                            {this.state.capturing
                                ? t('tools.gsc.exports.capturing')
                                : t('tools.gsc.exports.image')}
                        </button>
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handlePrint}
                            disabled={!ready}
                        >
                            <i className="icon-fi_download" />
                            {t('tools.gsc.exports.full_page')}
                        </button>
                    </div>
                </div>

                {!this.credentialsDomainId && (
                    <div className="gsc-tool-banner error">
                        {t('tools.gsc.controls.no_credentials')}
                    </div>
                )}

                <ScopeControls
                    draft={this.state.draft}
                    scope={scope}
                    applied={applied}
                    open={this.state.controlsOpen}
                    properties={this.state.properties}
                    loadingProperties={this.state.loadingProperties}
                    propertiesError={this.state.propertiesError}
                    latestDate={this.state.latestDate}
                    loading={this.state.loading}
                    onChange={this.handleDraftChange}
                    onApply={this.handleApply}
                    onRefresh={this.handleRefresh}
                    onToggle={this.handleToggleControls}
                    extraScopeLabels={[t(`tools.audience_appearance.segments.${scope.segment}`)]}
                >
                    <ExtraControls draft={this.state.draft} onChange={this.handleDraftChange} />
                </ScopeControls>

                {!applied ? (
                    <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                        <div className="card-body gsc-tool-load-state">
                            <span className="gsc-tool-load-icon" aria-hidden="true">
                                <i className="icon-globe" />
                            </span>
                            <h4>{t('tools.audience_appearance.idle_title')}</h4>
                            <p>{t('tools.audience_appearance.idle')}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <CoverageStrip
                            scope={applied}
                            current={{
                                status: this.state.segments.status,
                                coverage: this.state.trend.current?.coverage ?? null,
                                error: this.state.segments.error,
                            }}
                            baseline={
                                applied.baseline
                                    ? {
                                          status: this.state.segments.status,
                                          coverage: this.state.trend.baseline?.coverage ?? null,
                                          error: this.state.segments.error,
                                      }
                                    : null
                            }
                            fetchedAt={this.state.fetchedAt}
                            statusNote={t('tools.audience_appearance.coverage_note')}
                        />

                        <SegmentTable
                            scope={applied}
                            status={this.state.segments.status}
                            error={this.state.segments.error}
                            rows={this.state.segments.rows}
                            returnedRows={this.state.segments.returnedRows}
                            selectedKey={this.state.selectedKey}
                            onSelect={this.handleSelectSegment}
                            onExport={this.handleExport}
                        />

                        {/* The block a picked segment fills, wrapped so the first pick can bring
                            it into view. */}
                        <div ref={this.trendRef}>
                            <SegmentTrend
                                scope={applied}
                                status={this.state.trend.status}
                                error={this.state.trend.error}
                                selectedKey={this.state.selectedKey}
                                current={this.state.trend.current}
                                baseline={this.state.trend.baseline}
                                metric={this.metric()}
                                onMetric={this.handleMetric}
                                onClear={this.handleClearSegment}
                            />
                        </div>

                        <AppearanceContributors
                            scope={applied}
                            status={this.state.appearances.status}
                            error={this.state.appearances.error}
                            appearances={this.state.appearances.rows}
                            selected={this.state.selectedAppearance}
                            cleared={this.state.appearanceCleared}
                            pages={this.state.pages}
                            onSelect={this.handleSelectAppearance}
                            onClear={this.handleClearAppearance}
                        />

                        <SegmentCoverage
                            scope={applied}
                            status={this.state.segments.status}
                            returnedRows={this.state.segments.returnedRows}
                            exportable={ready}
                            onExport={this.handleExport}
                        />

                        <p className="gsc-tool-table-note">
                            {t('tools.audience_appearance.footnote', {
                                period: formatPeriod(applied.current),
                            })}
                        </p>
                    </>
                )}
            </div>
        );
    }
}

export default withTranslation()(AudienceAppearance);
